import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "onBlur", "onClick", "onFocus", "onKeyDown"];
import React, { forwardRef, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import IconChevronDown from '../Icon/icons/IconChevronDown';
import Flex from '../Flex/Flex';
import { AccordionHeaderMixin } from '../../mixins/components/AccordionHeaderMixin';
import AccordionItemContext from '../../js/AccordionItemContext';
export var StyledAccordionHeader = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledAccordionHeader",
  componentId: "c11n-8-64-0__sc-1aql0oo-0"
})(["", ""], AccordionHeaderMixin);
/**
 * The `Accordion.Header` component is used by the
 * [`Accordion.Item`](#/More%20components?id=accordion) component.
 *
 * **This component should not be used on its own.**
 *
 * @since 8.43.0
 */

var AccordionHeader = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
      onBlur = _ref.onBlur,
      onClick = _ref.onClick,
      onFocus = _ref.onFocus,
      onKeyDown = _ref.onKeyDown,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useContext = useContext(AccordionItemContext),
      expanded = _useContext.expanded,
      headerId = _useContext.headerId,
      panelId = _useContext.panelId,
      ref = _useContext.ref,
      onBlurContext = _useContext.onBlur,
      onClickContext = _useContext.onClick,
      onFocusContext = _useContext.onFocus,
      onKeyDownContext = _useContext.onKeyDown;

  var innerRef = useCallback(function (element) {
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else {
        // eslint-disable-next-line no-param-reassign
        forwardedRef.current = element;
      }
    }

    if (ref) {
      ref(element);
    }
  }, [ref, forwardedRef]);
  var onBlurHandler = useCallback(function (event) {
    onBlurContext(event);

    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  }, [onBlur, onBlurContext]);
  var onClickHandler = useCallback(function (event) {
    onClickContext(event);

    if (typeof onClick === 'function') {
      onClick(event);
    }
  }, [onClick, onClickContext]);
  var onFocusHandler = useCallback(function (event) {
    onFocusContext(event);

    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  }, [onFocus, onFocusContext]);
  var onKeyDownHandler = useCallback(function (event) {
    onKeyDownContext(event);

    if (typeof onKeyDown === 'function') {
      onKeyDown(event);
    }
  }, [onKeyDown, onKeyDownContext]);
  return /*#__PURE__*/React.createElement(StyledAccordionHeader, _extends({
    ref: innerRef,
    "aria-controls": panelId,
    "aria-expanded": expanded,
    id: headerId,
    tabIndex: 0,
    onBlur: onBlurHandler,
    onClick: onClickHandler,
    onFocus: onFocusHandler,
    onKeyDown: onKeyDownHandler
  }, rest), /*#__PURE__*/React.createElement(IconChevronDown, {
    size: "xs",
    marginRight: "sm"
  }), /*#__PURE__*/React.createElement(Flex, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexGrow: 1
  }, children));
});
AccordionHeader.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,

  /**
   * @ignore
   */
  onBlur: PropTypes.func,

  /**
   * @ignore
   */
  onClick: PropTypes.func,

  /**
   * @ignore
   */
  onFocus: PropTypes.func,

  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,

  /**
   * A [role](https://www.w3.org/TR/wai-aria-1.2/#roles) is required for assistive
   * technologies to announce the header properly. Header uses the
   * [button](https://www.w3.org/TR/wai-aria-1.2/#region) role.
   */
  role: PropTypes.string
} : {};
AccordionHeader.defaultProps = {
  role: 'button'
};
AccordionHeader.displayName = 'Accordion.Header';
/** @component */

export default AccordionHeader;